import { ComputeEngine } from "@cortex-js/compute-engine";
// eslint seems to be wrong here. VS Code and tsc both agree that this is fine.
// eslint-disable-next-line import/no-unresolved
import { Expression } from "@cortex-js/compute-engine/dist/types/math-json";

class MathError extends Error {
  constructor(
    message: string,
    public readonly data: unknown,
  ) {
    super(message);
    this.name = "MathError";

    this.data = data;
  }
}

const getBoxedExprs = (
  actual: string | null | undefined,
  expected: string | null | undefined,
  options?: { ignoreErrors?: boolean },
) => {
  const ce = new ComputeEngine();

  const boxedActual = ce.parse(actual ?? "", { canonical: false });
  const boxedExpected = ce.parse(expected ?? "", { canonical: false });

  if (
    (boxedActual.errors.length || boxedExpected.errors.length) &&
    !options?.ignoreErrors
  ) {
    throw new MathError("Error Parsing Latex", {
      parseError: {
        actual: boxedActual.errors,
        expected: boxedExpected.errors,
      },
      parsed: {
        actual: boxedActual.toJSON(),
        expected: boxedExpected.toJSON(),
      },
    });
  }

  return {
    boxedActual,
    boxedExpected,
  };
};

export const debugMath = (latex: string) => {
  const ce = new ComputeEngine();

  const boxed = ce.parse(latex, { canonical: false });

  return {
    latex,
    errors: boxed.errors,
    parsed: boxed.toJSON(),
    canonical: boxed.canonical.toJSON(),
    simplified: boxed.canonical.simplify().toJSON(),
  };
};

export const scoreSymbolic = (actual: string, expected: string) => {
  try {
    const { boxedActual, boxedExpected } = getBoxedExprs(actual, expected, {
      ignoreErrors: true,
    });

    return boxedActual.canonical
      .simplify()
      .isEqual(boxedExpected.canonical.simplify());
  } catch (e: unknown) {
    console.error(JSON.stringify(e, null, 2));
    return false;
  }
};

export const scoreValue = (actual: string, expected: string) => {
  try {
    const { boxedActual, boxedExpected } = getBoxedExprs(actual, expected, {
      ignoreErrors: true,
    });

    return boxedActual.isSame(boxedExpected);
  } catch (e: unknown) {
    // this was too noisy in datadog, so we're turning it off for now
    // console.error(e);
    return false;
  }
};

export const scoreValueLooser = (actual: string, expected: string) => {
  try {
    const { boxedActual, boxedExpected } = getBoxedExprs(actual, expected, {
      ignoreErrors: true,
    });

    return boxedActual.canonical.isSame(boxedExpected.canonical);
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};

export const scoreValueEvenLooser = (actual: string, expected: string) => {
  try {
    const { boxedActual, boxedExpected } = getBoxedExprs(actual, expected, {
      ignoreErrors: true,
    });

    return boxedActual.canonical
      .simplify()
      .isSame(boxedExpected.canonical.simplify());
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};

export const scoreFormatIsNumber = (actual: string, expected: string) => {
  try {
    const { boxedActual } = getBoxedExprs(actual, expected);

    // MathJSON for basic constants are just numbers
    // Anything more complex is an array of stuff
    return typeof boxedActual.json === "number";
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};

// Fractions are of the form:
// [
//    "Rational" | "Divide",   (Divide if it has decimals in it I think)
//    number | string,         (string if it's a variable)
//    number | string,
// ]
const _isFractionJson = (json: Expression) => {
  return (
    Array.isArray(json) &&
    json.length === 3 &&
    (json[0] === "Rational" || json[0] === "Divide") &&
    (typeof json[1] === "number" || typeof json[1] === "string") &&
    (typeof json[2] === "number" || typeof json[2] === "string")
  );
};

export const scoreFormatIsProperFraction = (
  actual: string,
  expected: string,
) => {
  try {
    const { boxedActual } = getBoxedExprs(actual, expected);

    return _isFractionJson(boxedActual.json);
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};

export const scoreFormatIsMixedFraction = (
  actual: string,
  expected: string,
) => {
  try {
    const { boxedActual } = getBoxedExprs(actual, expected);

    // mixed numbers are of the form:
    // ["Add", number, [FRACTION_JSON] ]
    return (
      Array.isArray(boxedActual.json) &&
      boxedActual.json.length === 3 &&
      boxedActual.json[0] === "Add" &&
      typeof boxedActual.json[1] == "number" &&
      _isFractionJson(boxedActual.json[2])
    );
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};

export const scoreFormatIsFraction = (actual: string, expected: string) => {
  try {
    return (
      scoreFormatIsProperFraction(actual, expected) ||
      scoreFormatIsMixedFraction(actual, expected)
    );
  } catch (e: unknown) {
    console.error(e);
    return false;
  }
};
